import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, Tag, Typography, Empty, Spin, Row, Col, Space, message } from 'antd';
import { CheckCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Test } from '../types';
import {
  TEST_DIFFICULTY_COLOR,
  TEST_DIFFICULTY_LABEL,
} from '../constants/testDifficulty';
import { getApiErrorMessage } from '../utils/apiError';

const { Title, Paragraph } = Typography;

const TestList: React.FC = () => {
  const [tests, setTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { token, user, loading: authLoading } = useAuth();

  const fetchTests = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const response = await api.get('/tests/');
      setTests(response.data ?? []);
    } catch (error: any) {
      console.error('Ошибка загрузки тестов:', error);
      const msg = error.response?.status === 401
        ? 'Требуется повторный вход'
        : getApiErrorMessage(error, 'Не удалось загрузить список тестов');
      setLoadError(msg);
      setTests([]);
      if (error.response?.status === 401) {
        message.warning(msg);
      } else {
        message.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !token || !user?.id) {
      return;
    }
    fetchTests();
  }, [authLoading, token, user?.id, fetchTests]);

  const getBlockLabel = (block: string) => {
    return block === 'products' ? t('tests.block.products') : t('tests.block.vehicles');
  };

  const getBlockColor = (block: string) => {
    return block === 'products' ? 'blue' : 'green';
  };

  const getAssignmentStatus = (test: Test) => (test.assignment_status || '').toLowerCase();

  const getAssignmentStatusLabel = (test: Test) => {
    const status = getAssignmentStatus(test);
    if (status === 'completed') return 'Завершён';
    if (status === 'started') return 'Начат';
    if (status === 'assigned') return 'Назначен';
    if (status === 'expired') return 'Истёк';
    return '';
  };

  const getAssignmentStatusColor = (test: Test) => {
    const status = getAssignmentStatus(test);
    if (status === 'completed') return 'success';
    if (status === 'started') return 'processing';
    if (status === 'assigned') return 'gold';
    if (status === 'expired') return 'error';
    return 'default';
  };

  const isCompletedAssignment = (test: Test) => getAssignmentStatus(test) === 'completed';
  const isCompletedByUser = (test: Test) =>
    test.is_completed_by_user === true || isCompletedAssignment(test) || Boolean(test.completed_attempt_id);
  const canStartTest = (test: Test) => test.can_start !== false && test.can_open !== false;
  const isRetakeAuthorized = (test: Test) => isCompletedByUser(test) && canStartTest(test);
  const getCompletionLabel = (test: Test) => {
    if (test.user_is_passed === false) return 'Не сдан';
    return 'Пройден';
  };
  const getCompletionColor = (test: Test) => (test.user_is_passed === false ? 'error' : 'success');
  const formatScore = (score?: number | null) =>
    typeof score === 'number' && Number.isFinite(score) ? `${score.toFixed(1)}%` : null;
  const getQuestionWord = (count: number) => (
    count % 10 === 1 && count % 100 !== 11 ? 'вопросом' : 'вопросами'
  );
  const getQuestionDescription = (test: Test) => {
    const questionsCount = test.questions_count || 0;
    const description = test.description?.trim();
    if (description?.toLowerCase().startsWith('тест с ') && description.toLowerCase().includes('вопрос')) {
      return questionsCount > 0 ? `Тест с ${questionsCount} ${getQuestionWord(questionsCount)}` : 'Тест без вопросов';
    }
    return description || t('tests.description.empty');
  };

  if (loading && tests.length === 0) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-guide="test-catalog" className="tests-page-header">
        <Title level={1} className="tests-page-title">{t('tests.title')}</Title>
        <Paragraph className="tests-page-subtitle">{t('tests.subtitle')}</Paragraph>
      </div>
      {loadError && (
        <div style={{ marginBottom: 16 }}>
          <Space>
            <span style={{ color: 'var(--ant-color-error)' }}>{loadError}</span>
            <Button type="primary" size="small" onClick={fetchTests}>Повторить</Button>
          </Space>
        </div>
      )}

      {tests.length === 0 ? (
        <Empty description={t('tests.noTests')} />
      ) : (
        <Row gutter={[24, 24]} justify="center">
          {tests.map((test) => {
            const completed = isCompletedByUser(test);
            const retakeAuthorized = isRetakeAuthorized(test);
            const completedLocked = completed && !retakeAuthorized;
            const scoreLabel = formatScore(test.user_score);

            return (
            <Col key={test.id} xs={24} sm={24} md={12} lg={8} xl={6}>
              <Card data-guide="test-card"
                title={
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: 8 }}>
                      <Tag color={getBlockColor(test.block)}>{getBlockLabel(test.block)}</Tag>
                      {test.assignment_id && (
                        <Tag color={getAssignmentStatusColor(test)}>{getAssignmentStatusLabel(test)}</Tag>
                      )}
                      {completed && (
                        <Tag color={getCompletionColor(test)}>{getCompletionLabel(test)}</Tag>
                      )}
                      {retakeAuthorized && (
                        <Tag color="processing">Пересдача назначена</Tag>
                      )}
                    </div>
                    <span
                      style={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        display: 'block',
                        lineHeight: 1.4,
                        fontWeight: 600,
                        fontSize: '15px',
                      }}
                      title={test.title}
                    >
                      {test.title}
                    </span>
                  </div>
                }
                actions={[
                  <Space key="actions" direction="vertical" style={{ width: '100%', padding: '0 16px' }}>
                    {completedLocked ? (
                      <Button
                        key="completed"
                        type="default"
                        icon={<CheckCircleOutlined />}
                        disabled
                        style={{ width: '100%' }}
                      >
                        {getCompletionLabel(test)}
                      </Button>
                    ) : (
                      <Button
                        data-guide="test-open"
                        key="start"
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        disabled={!canStartTest(test)}
                        onClick={() => navigate(`/tests/${test.id}`)}
                        style={{ width: '100%' }}
                      >
                        {retakeAuthorized ? 'Пройти повторно' : canStartTest(test) ? t('tests.start') : 'Недоступен'}
                      </Button>
                    )}
                  </Space>,
                ]}
                style={{ height: '100%', opacity: completedLocked ? 0.78 : 1 }}
                headStyle={{ overflow: 'visible' }}
                styles={{
                  header: { overflow: 'visible' },
                  body: { overflow: 'visible' },
                }}
              >
                <Paragraph ellipsis={{ rows: 3 }}>{getQuestionDescription(test)}</Paragraph>
                <div style={{ marginTop: 16 }}>
                  <Tag>{t('tests.questions')}: {test.questions_count || 0}</Tag>
                  <Tag>{t('tests.time')}: {test.time_limit_minutes} {t('tests.minutes')}</Tag>
                  {test.difficulty && (
                    <Tag color={TEST_DIFFICULTY_COLOR[test.difficulty]}>
                      Сложность: {TEST_DIFFICULTY_LABEL[test.difficulty]}
                    </Tag>
                  )}
                  {scoreLabel && <Tag color="default">Результат: {scoreLabel}</Tag>}
                </div>
              </Card>
            </Col>
          );
          })}
        </Row>
      )}
    </Layout>
  );
};

export default TestList;
